import React from 'react';
import { motion } from 'framer-motion';

const iconMap = {
  competition: (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 3h8l4 4v10l-4 4H8l-4-4V7z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 12h8" strokeLinecap="round" />
    </svg>
  ),
  workshop: (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="6" width="16" height="12" rx="3" />
      <path d="M8 10h8M8 14h5" strokeLinecap="round" />
    </svg>
  ),
  conference: (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 4h14v16H5z" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 8h8" strokeLinecap="round" />
    </svg>
  ),
  social: (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="10" r="3" />
      <path d="M5 20c0-3 2.5-5 7-5s7 2 7 5" strokeLinecap="round" />
    </svg>
  ),
};

const events = [
  {
    title: 'Hackathones',
    description: 'Competencias de 48 horas donde desarrollas soluciones innovadoras para desafíos reales en ingeniería y tecnología.',
    type: 'Competencia',
    icon: iconMap.competition,
  },
  {
    title: 'Charlas Técnicas',
    description: 'Sesiones con expertos en distintas ramas de IEEE para explorar tendencias e innovaciones en tu área de interés.',
    type: 'Conferencia',
    icon: iconMap.conference,
  },
  {
    title: 'Campeonato Interno Deportivo',
    description: 'Actividades deportivas que fomentan la integración, compañerismo y bienestar entre miembros de IEEE ESPOCH.',
    type: 'Social',
    icon: iconMap.social,
  },
  {
    title: 'Workshops y Entrenamientos',
    description: 'Talleres prácticos de alto nivel en tecnologías emergentes, programación, automatización y más.',
    type: 'Taller',
    icon: iconMap.workshop,
  },
];

const Events: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8 },
    },
  };

  return (
    <section className="relative overflow-hidden bg-white py-24 sm:py-32 lg:py-40" id="events">
      {/* Ambient background optimized for light mode */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 right-1/3 w-[600px] h-[600px] rounded-full blur-[140px] opacity-[0.05] bg-gradient-to-l from-cyan-400/30 to-transparent" />
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full blur-[130px] opacity-[0.04] bg-gradient-to-t from-indigo-400/20 to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 sm:px-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="space-y-16"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="space-y-10 max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 shadow-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-600 shadow-[0_0_8px_rgba(8,145,178,0.3)]" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-slate-500">
                Eventos
              </span>
            </div>

            <div className="space-y-6">
              <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.12] tracking-tight text-slate-900">
                Experiencias técnicas y{' '}
                <span className="text-cyan-600">
                  networking
                </span>
                {' '}de primer nivel
              </h2>
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed font-normal max-w-2xl">
                Participa en actividades diseñadas para amplificar tu perfil profesional y conectar con el ecosistema IEEE global.
              </p>
            </div>
          </motion.div>

          {/* Events grid */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8"
          >
            {events.map((event, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -8 }}
                className="group relative rounded-[2.5rem] p-8 lg:p-10 border border-slate-100 bg-white transition-all duration-500 hover:border-cyan-200 hover:shadow-premium"
              >
                <div className="relative space-y-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-slate-50 text-cyan-600 border border-slate-100 transition-all duration-500 group-hover:bg-cyan-50 group-hover:border-cyan-100">
                      {event.icon}
                    </div>
                    <span className="rounded-full border border-slate-100 bg-slate-50 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                      {event.type}
                    </span>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-2xl font-black text-slate-900 leading-snug">
                      {event.title}
                    </h3>
                    <p className="text-base text-slate-500 leading-relaxed font-normal">
                      {event.description}
                    </p>
                  </div>

                  <motion.a
                    href="#cta"
                    whileHover={{ x: 6 }}
                    className="inline-flex items-center gap-3 text-sm font-bold uppercase tracking-[0.15em] text-cyan-600 hover:text-cyan-500 transition-colors duration-300"
                  >
                    Más información
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </motion.a>
                </div>

                <div className="absolute bottom-0 left-8 right-8 h-1 bg-gradient-to-r from-cyan-500/40 to-transparent w-0 group-hover:w-[calc(100%-64px)] transition-all duration-500 rounded-full" />
              </motion.div>
            ))}
          </motion.div>

          {/* Call-to-propose section */}
          <motion.div
            variants={itemVariants}
            className="rounded-[2.5rem] border border-slate-100 bg-slate-50/50 p-10 lg:p-16 shadow-sm"
          >
            <div className="flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-12">
              <div className="space-y-4 flex-1">
                <h3 className="text-3xl lg:text-4xl font-black text-slate-900 leading-tight">
                  ¿Tienes una idea para un evento?
                </h3>
                <p className="text-lg text-slate-600 leading-relaxed font-normal">
                  IEEE ESPOCH apoya propuestas innovadoras de workshops, hackathons y conferencias que impulsen el talento técnico local.
                </p>
              </div>
              <motion.a
                href="#cta"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center px-10 py-4 rounded-xl font-bold text-sm uppercase tracking-[0.2em] bg-cyan-600 text-white transition-all duration-300 shadow-premium hover:bg-cyan-500 whitespace-nowrap"
              >
                Proponer evento
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Events;
